import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Activity, BarChart3, Building2, CalendarDays, ChevronDown, GraduationCap, Globe2,
  LayoutDashboard, LineChart, Lock, type LucideIcon, Medal, Menu, MessageCircle, MessageSquare,
  Search, SlidersHorizontal, Trophy, UserCircle, UserPlus, Users, UsersRound, X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type Leaf = { to: string; label: string; icon: LucideIcon; end?: boolean };
type Group = { label: string; icon: LucideIcon; base: string; children: Leaf[] };
type Item = Leaf | Group;
const isGroup = (i: Item): i is Group => "children" in i;

// Funzioni dell'app Playtomic (raggruppate in un dropdown per non affollare la barra)
const PLAYTOMIC: Leaf[] = [
  { to: "/playtomic/players", label: "Ricerca giocatori", icon: Search },
  { to: "/playtomic/chat", label: "Chat", icon: MessageSquare },
  { to: "/playtomic/matches-public", label: "Partite pubbliche", icon: Globe2 },
  { to: "/playtomic/matches-private", label: "Partite private", icon: Lock },
  { to: "/playtomic/clubs", label: "Club", icon: Building2 },
  { to: "/playtomic/community", label: "Community", icon: UsersRound },
  { to: "/playtomic/lessons", label: "Lezioni", icon: GraduationCap },
  { to: "/playtomic/rankings", label: "Classifiche", icon: BarChart3 },
  { to: "/playtomic/stats", label: "Statistiche", icon: LineChart },
  { to: "/playtomic/profile", label: "Profilo", icon: UserCircle },
];

const NAV: Item[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/tournaments", label: "Tornei", icon: Trophy },
  { to: "/gare", label: "Gare", icon: Medal },
  { to: "/messaging", label: "Messaging", icon: MessageCircle },
  { to: "/community", label: "Community", icon: UserPlus },
  { to: "/calendar", label: "Calendario", icon: CalendarDays },
  { to: "/clients", label: "Clienti", icon: Users },
  { label: "Playtomic", icon: SlidersHorizontal, base: "/playtomic", children: PLAYTOMIC },
  { to: "/automations", label: "Sistemi", icon: Activity },
];

const leafCls = (active: boolean) =>
  cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
    active ? "bg-moving-500 text-white" : "text-slate-600 hover:bg-slate-100");

function DesktopGroup({ g, path }: { g: Group; path: string }) {
  const [open, setOpen] = useState(false);
  const active = path.startsWith(g.base);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className={leafCls(active)} title={g.label} onClick={() => setOpen((v) => !v)}>
        <g.icon className="h-4 w-4" /><span className="hidden lg:inline">{g.label}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 min-w-[200px] rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          {g.children.map((c) => (
            <NavLink key={c.to} to={c.to} onClick={() => setOpen(false)}
              className={({ isActive }) => cn("flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
                isActive ? "bg-moving-500 text-white" : "text-slate-700 hover:bg-slate-100")}>
              <c.icon className="h-4 w-4" />{c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function Topbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]); // chiudi menu mobile al cambio rotta

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
      {/* Riga 1: logo + titolo (+ hamburger su mobile) */}
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="https://www.movingpadel.it/img/moving_padel_new.png" alt="Moving Padel Monza"
            className="h-9 w-auto shrink-0" loading="eager" />
          <span className="text-sm font-semibold text-slate-900">Control Center</span>
        </Link>

        <button type="button" aria-label="Menu" onClick={() => setOpen((v) => !v)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 md:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Riga 2: nav desktop, sempre sotto logo e titolo */}
      <div className="hidden border-t border-slate-100 md:block">
        <nav className="mx-auto flex max-w-6xl flex-wrap items-center gap-1 px-4 py-1.5">
          {NAV.map((item) =>
            isGroup(item)
              ? <DesktopGroup key={item.label} g={item} path={loc.pathname} />
              : <NavLink key={item.to} to={item.to} end={item.end} title={item.label}
                  className={({ isActive }) => leafCls(isActive)}>
                  <item.icon className="h-4 w-4" /><span className="hidden lg:inline">{item.label}</span>
                </NavLink>,
          )}
        </nav>
      </div>

      {/* Mobile nav */}
      {open ? (
        <nav className="border-t border-slate-200 bg-white px-4 py-2 md:hidden">
          <ul className="flex flex-col gap-1">
            {NAV.map((item) =>
              isGroup(item) ? (
                <li key={item.label}>
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{item.label}</div>
                  {item.children.map((c) => (
                    <NavLink key={c.to} to={c.to} onClick={() => setOpen(false)}
                      className={({ isActive }) => cn("flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
                        isActive ? "bg-moving-500 text-white" : "text-slate-700 hover:bg-slate-100")}>
                      <c.icon className="h-4 w-4" />{c.label}
                    </NavLink>
                  ))}
                </li>
              ) : (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end} onClick={() => setOpen(false)}
                    className={({ isActive }) => cn("flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
                      isActive ? "bg-moving-500 text-white" : "text-slate-700 hover:bg-slate-100")}>
                    <item.icon className="h-4 w-4" />{item.label}
                  </NavLink>
                </li>
              ),
            )}
          </ul>
        </nav>
      ) : null}
    </header>
  );
}
